
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Response} from '../core/Response';
import {FieldModelResponse} from '../core/Response';
import {JournalBusinessRecord} from '../core/JournalBusinessRecord';
import {FieldModelJournalBusinessRecord} from '../core/JournalBusinessRecord';

/**
 * JournalBusinessResponse struct
 */
class JournalBusinessResponse extends Response {
  /**
   * Initialize struct
   * @param {!Response=} parent
   * @param {!Array=} records
   * @constructor
   */
  constructor (parent = new Response(), records = []) {
    super()
    super.copy(parent)
    this.Records = records
  }

  /**
   * Copy struct (shallow copy)
   * @this {!JournalBusinessResponse}
   * @param {!JournalBusinessResponse} other Other struct
   * @returns {!JournalBusinessResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Records != null) {
      this.Records = []
      for (let item of other.Records) {
        if (item != null) {
          let tempItem
          tempItem = JournalBusinessRecord.fromObject(item)
          this.Records.push(tempItem)
        } else {
          this.Records.push(null)
        }
      }
    } else {
      this.Records = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!JournalBusinessResponse}
   * @returns {!JournalBusinessResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new JournalBusinessResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new JournalBusinessResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!JournalBusinessResponse}
   * @param {!JournalBusinessResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof JournalBusinessResponse)) {
      throw new TypeError('Instance of JournalBusinessResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!JournalBusinessResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Records: ((this.Records != null) ? Array.from(this.Records, item => ((item != null) ? item : null)) : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return JournalBusinessResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!JournalBusinessResponse} other Object value
   * @returns {!JournalBusinessResponse} Created struct
   */
  static fromObject (other) {
    return new JournalBusinessResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!JournalBusinessResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return JournalBusinessResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!JournalBusinessResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6461
  }
}

export { JournalBusinessResponse };

/**
 * Fast Binary Encoding JournalBusinessResponse field model
 */
class FieldModelJournalBusinessResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelResponse(buffer, 4 + 4)
    this.Records = new fbe.FieldModelVector(new FieldModelJournalBusinessRecord(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
  }

  /**
   * Get the core.Response field model
   * @this {!FieldModelJournalBusinessResponse}
   * @returns {!FieldModelResponse} core.Response field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelJournalBusinessResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelJournalBusinessResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Records.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelJournalBusinessResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Records.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelJournalBusinessResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelJournalBusinessResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelJournalBusinessResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6461
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelJournalBusinessResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelJournalBusinessResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Records.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Records.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Records.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelJournalBusinessResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelJournalBusinessResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelJournalBusinessResponse}
   * @param {!JournalBusinessResponse} fbeValue Default value, defaults is new JournalBusinessResponse()
   * @returns {!JournalBusinessResponse} JournalBusinessResponse value
   */
  get (fbeValue = new JournalBusinessResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelJournalBusinessResponse}
   * @param {!JournalBusinessResponse} fbeValue JournalBusinessResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Records.fbeSize) <= fbeStructSize) {
      this.Records.get(fbeValue.Records)
    } else {
      fbeValue.Records.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Records.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelJournalBusinessResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelJournalBusinessResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelJournalBusinessResponse}
   * @param {!JournalBusinessResponse} fbeValue JournalBusinessResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelJournalBusinessResponse}
   * @param {JournalBusinessResponse} fbeValue JournalBusinessResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Records.set(fbeValue.Records)
  }
}

export { FieldModelJournalBusinessResponse };

/**
 * Fast Binary Encoding JournalBusinessResponse model
 */
class JournalBusinessResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelJournalBusinessResponse(this.buffer, 4)
  }

  /**
   * Get the JournalBusinessResponse model
   * @this {!JournalBusinessResponseModel}
   * @returns {!FieldModelJournalBusinessResponse} model JournalBusinessResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!JournalBusinessResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!JournalBusinessResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return JournalBusinessResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!JournalBusinessResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelJournalBusinessResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!JournalBusinessResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!JournalBusinessResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!JournalBusinessResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!JournalBusinessResponseModel}
   * @param {!JournalBusinessResponse} value JournalBusinessResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!JournalBusinessResponseModel}
   * @param {!JournalBusinessResponse} value JournalBusinessResponse value, defaults is new JournalBusinessResponse()
   * @return {!object} Deserialized JournalBusinessResponse value and its size
   */
  deserialize (value = new JournalBusinessResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new JournalBusinessResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new JournalBusinessResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!JournalBusinessResponseModel}
   * @param {!number} prev Previous JournalBusinessResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { JournalBusinessResponseModel };
